import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLogSheet } from "@/contexts/LogSheetContext";
import { timeAgo } from "@/lib/timeAgo";
import { FButton } from "@/components/forkd/FButton";
import { CommentSheet } from "@/components/forkd/CommentSheet";
import { shareCard } from "@/lib/shareCard";
import { rarityColour, type Rarity } from "@/lib/achievementEngine";

type FeedPost = {
  id: string;
  user_id: string;
  visit_id: string;
  content: string | null;
  photo_urls: string[] | null;
  achievement_ids: string[] | null;
  like_count: number;
  comment_count: number;
  is_public: boolean;
  created_at: string;
  user: {
    username: string;
    display_name: string;
    avatar_url: string | null;
  } | null;
  visit: {
    restaurant: {
      name: string;
      restaurant_cuisine_types:
        | { cuisine_types: { name: string; emoji: string | null } | null }[]
        | null;
      neighbourhood: { name: string } | null;
    } | null;
  } | null;
  achievements?: BadgeChip[];
};

type BadgeChip = {
  id: string;
  name: string;
  description: string;
  rarity: Rarity;
  colour_hex: string;
};

const PAGE_SIZE = 20;

export default function Feed() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [activeCommentPost, setActiveCommentPost] = useState<string | null>(null);
  const [followingIds, setFollowingIds] = useState<string[]>([]);
  const sentinel = useRef<HTMLDivElement>(null);
  const { openSheet } = useLogSheet();
  const [actionPost, setActionPost] = useState<FeedPost | null>(null);
  const pressTimer = useRef<number | null>(null);


  const load = useCallback(
    async (offset: number) => {
      if (!user) return;

      // Following ids (+ self)
      let followIds = followingIds;
      if (offset === 0) {
        const { data: follows } = await supabase
          .from("follows")
          .select("following_id")
          .eq("follower_id", user.id);
        followIds = [user.id, ...(follows ?? []).map((f: any) => f.following_id)];
        setFollowingIds(followIds);
      }
      if (followIds.length === 0) followIds = [user.id];

      const { data, error } = await supabase
        .from("feed_posts")
        .select(
          "id, user_id, visit_id, content, photo_urls, achievement_ids, like_count, comment_count, is_public, created_at, user:profiles(username, display_name, avatar_url), visit:visits(restaurant:restaurants(name, neighbourhood:neighbourhoods(name), restaurant_cuisine_types(cuisine_types(name, emoji))))",
        )
        .in("user_id", followIds)
        .or(`is_public.eq.true,user_id.eq.${user.id}`)
        .order("created_at", { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) {
        console.error(error);
        return;
      }
      const rows = (data ?? []) as unknown as FeedPost[];

      // Achievements per post
      const allAchIds = Array.from(
        new Set(rows.flatMap((p) => p.achievement_ids ?? [])),
      );
      let badgeLookup: Record<string, BadgeChip> = {};
      if (allAchIds.length > 0) {
        const { data: achs } = await supabase
          .from("achievements")
          .select("id, name, description, rarity, colour_hex")
          .in("id", allAchIds);
        for (const a of achs ?? []) {
          badgeLookup[a.id] = a as BadgeChip;
        }
      }
      for (const p of rows) {
        p.achievements = (p.achievement_ids ?? [])
          .map((id) => badgeLookup[id])
          .filter(Boolean);
      }

      // Likes by current user
      const ids = rows.map((p) => p.id);
      if (ids.length > 0) {
        const { data: likes } = await supabase
          .from("post_likes")
          .select("post_id")
          .eq("user_id", user.id)
          .in("post_id", ids);
        const liked = new Set(likedIds);
        for (const l of likes ?? []) liked.add((l as any).post_id);
        setLikedIds(liked);
      }

      setPosts((prev) => (offset === 0 ? rows : [...prev, ...rows]));
      setHasMore(rows.length === PAGE_SIZE);
    },
    [user, followingIds, likedIds],
  );

  useEffect(() => {
    setLoading(true);
    load(0).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinel.current || loading) return;
    const el = sentinel.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          setLoadingMore(true);
          load(posts.length).finally(() => setLoadingMore(false));
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, posts.length, loading, load]);

  const toggleLike = async (post: FeedPost) => {
    if (!user) return;
    const isLiked = likedIds.has(post.id);
    // optimistic
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (isLiked) next.delete(post.id);
      else next.add(post.id);
      return next;
    });
    setPosts((prev) =>
      prev.map((p) =>
        p.id === post.id
          ? { ...p, like_count: p.like_count + (isLiked ? -1 : 1) }
          : p,
      ),
    );
    if (isLiked) {
      await supabase
        .from("post_likes")
        .delete()
        .eq("post_id", post.id)
        .eq("user_id", user.id);
    } else {
      await supabase
        .from("post_likes")
        .insert({ post_id: post.id, user_id: user.id });
    }
  };

  const share = async (p: FeedPost) => {
    const cuisine = p.visit?.restaurant?.restaurant_cuisine_types?.[0]?.cuisine_types;
    await shareCard({
      restaurantName: p.visit?.restaurant?.name ?? "A restaurant",
      photoUrl: p.photo_urls?.[0] ?? null,
      cuisineEmoji: cuisine?.emoji ?? undefined,
      cuisineName: cuisine?.name ?? undefined,
      neighbourhood: p.visit?.restaurant?.neighbourhood?.name ?? undefined,
      badges: (p.achievements ?? []).map((b) => ({
        name: b.name,
        colour: b.colour_hex,
      })),
      username: p.user?.username ?? "forkd",
    });
  };

  const deletePost = async (postId: string) => {
    const { error } = await supabase.from("feed_posts").delete().eq("id", postId);
    if (error) {
      toast.error(error.message);
      return;
    }
    setPosts((prev) => prev.filter((p) => p.id !== postId));
    toast.success("Post deleted");
  };

  if (loading) {
    return (
      <div className="px-4 pt-6 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-72 rounded-[var(--radius-md)] animate-pulse"
            style={{ backgroundColor: "var(--colour-surface-raised)" }}
          />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <div className="text-[64px] fkd-bob mb-3" aria-hidden>🍴</div>
        <h2 className="text-[20px] font-bold">Nothing here yet</h2>
        <p className="text-[14px] text-[var(--colour-text-secondary)] mt-1 mb-5 max-w-[280px]">
          Follow people to see their meals, or log your first eat to get started.
        </p>
        <div className="flex gap-2.5">
          <FButton variant="outline" onClick={() => nav("/onboarding")}>Find people</FButton>
          <FButton onClick={() => openSheet()}>Log a meal</FButton>
        </div>
      </div>
    );
  }

  const startPress = (p: FeedPost) => {
    if (pressTimer.current) window.clearTimeout(pressTimer.current);
    pressTimer.current = window.setTimeout(() => setActionPost(p), 500);
  };
  const endPress = () => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  return (
    <div className="px-4 pt-4 pb-6 space-y-5">
      {posts.map((p) => (
        <article
          key={p.id}
          onPointerDown={() => startPress(p)}
          onPointerUp={endPress}
          onPointerLeave={endPress}
          onPointerCancel={endPress}
          onContextMenu={(e) => e.preventDefault()}
          className="border overflow-hidden select-none"
          style={{
            borderColor: "#E5E2DC",
            borderWidth: "1px",
            borderRadius: "20px",
            backgroundColor: "var(--colour-surface)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header */}
          <Link
            to={`/u/${p.user?.username ?? ""}`}
            className="flex items-center gap-3 p-3"
          >
            <Avatar url={p.user?.avatar_url ?? null} name={p.user?.display_name ?? "?"} />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold truncate">
                {p.user?.display_name ?? "User"}
              </div>
              <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                @{p.user?.username}
              </div>
            </div>
            <div className="text-[12px] text-[var(--colour-text-secondary)] shrink-0">
              {timeAgo(p.created_at)}
            </div>
          </Link>

          {/* Restaurant row */}
          <div className="px-3 pb-2">
            <div className="text-[15px] font-semibold">
              {p.visit?.restaurant?.name ?? "Restaurant"}
            </div>
            <div className="text-[12px] text-[var(--colour-text-secondary)]">
              {(() => {
                const c = p.visit?.restaurant?.restaurant_cuisine_types?.[0]?.cuisine_types;
                const parts: string[] = [];
                if (c) parts.push(`${c.emoji ?? "🍽️"} ${c.name}`);
                if (p.visit?.restaurant?.neighbourhood?.name)
                  parts.push(p.visit.restaurant.neighbourhood.name);
                return parts.join(" · ");
              })()}
            </div>
          </div>

          {/* Photo (only when present) */}
          {p.photo_urls && p.photo_urls[0] && (
            <div className="px-3">
              <img
                src={p.photo_urls[0]}
                alt=""
                className="w-full aspect-[4/3] object-cover rounded-[var(--radius-md)]"
              />
            </div>
          )}

          {/* Caption */}
          {p.content && (
            <p className="px-3 pt-3 text-[14px] whitespace-pre-wrap break-words">
              {p.content}
            </p>
          )}

          {/* Achievement chips */}
          {p.achievements && p.achievements.length > 0 && (
            <div className="px-3 pt-3 flex gap-2 overflow-x-auto">
              {p.achievements.map((b) => (
                <BadgeChipView key={b.id} chip={b} />
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 px-3 py-3 mt-1">
            <button
              onClick={() => toggleLike(p)}
              className="flex items-center gap-1.5 min-h-[36px]"
              aria-label="Like"
            >
              <Heart
                size={20}
                fill={likedIds.has(p.id) ? rarityColour("rare") : "transparent"}
                color={
                  likedIds.has(p.id)
                    ? rarityColour("rare")
                    : "var(--colour-text-secondary)"
                }
              />
              <span className="text-[13px]">{p.like_count}</span>
            </button>
            <button
              onClick={() => setActiveCommentPost(p.id)}
              className="flex items-center gap-1.5 min-h-[36px]"
              aria-label="Comments"
            >
              <MessageCircle size={20} className="text-[var(--colour-text-secondary)]" />
              <span className="text-[13px]">{p.comment_count}</span>
            </button>
            <button
              onClick={() => share(p)}
              className="flex items-center gap-1.5 min-h-[36px] ml-auto"
              aria-label="Share"
            >
              <Share2 size={20} className="text-[var(--colour-text-secondary)]" />
            </button>
          </div>
        </article>
      ))}

      <div ref={sentinel} className="h-8" />
      {loadingMore && (
        <p className="text-center text-[13px] text-[var(--colour-text-secondary)]">
          Loading…
        </p>
      )}

      {activeCommentPost && (
        <CommentSheet
          postId={activeCommentPost}
          open
          onClose={() => setActiveCommentPost(null)}
          onAdded={() =>
            setPosts((prev) =>
              prev.map((p) =>
                p.id === activeCommentPost
                  ? { ...p, comment_count: p.comment_count + 1 }
                  : p,
              ),
            )
          }
        />
      )}

      {actionPost && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setActionPost(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full p-3"
            style={{
              backgroundColor: "var(--colour-surface)",
              borderTopLeftRadius: "var(--radius-lg)",
              borderTopRightRadius: "var(--radius-lg)",
              paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-1 pb-2">
              <div className="w-10 h-1 rounded-full" style={{ backgroundColor: "var(--colour-border)" }} />
            </div>
            <button
              onClick={() => {
                share(actionPost);
                setActionPost(null);
              }}
              className="w-full text-left h-12 px-3 rounded-[var(--radius-md)] text-[15px] font-medium"
            >
              Share post
            </button>
            {user?.id === actionPost.user_id && (
              <button
                onClick={() => {
                  deletePost(actionPost.id);
                  setActionPost(null);
                }}
                className="w-full text-left h-12 px-3 rounded-[var(--radius-md)] text-[15px] font-medium text-[var(--colour-danger,#ef4444)]"
              >
                Delete post
              </button>
            )}
            <button
              onClick={() => setActionPost(null)}
              className="w-full text-center h-12 mt-1 rounded-[var(--radius-md)] text-[14px] text-[var(--colour-text-secondary)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Avatar({ url, name }: { url: string | null; name: string }) {
  if (url)
    return (
      <img src={url} alt={name} className="w-10 h-10 rounded-[var(--radius-full)] object-cover" />
    );
  return (
    <div
      className="w-10 h-10 rounded-[var(--radius-full)] flex items-center justify-center text-[16px] font-semibold"
      style={{
        backgroundColor: "var(--colour-surface-raised)",
        color: "var(--colour-text-secondary)",
      }}
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

function BadgeChipView({ chip }: { chip: BadgeChip }) {
  const [open, setOpen] = useState(false);
  const rColour = rarityColour(chip.rarity);
  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-4 h-9 rounded-[var(--radius-full)] text-[13px] font-semibold"
        style={{
          backgroundColor: `${rColour}26`,
          color: "var(--colour-text-primary)",
        }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: rColour }}
        />
        {chip.name}
      </button>
      {open && (
        <div
          className="absolute z-10 top-9 left-0 w-56 p-3 rounded-[var(--radius-md)] border shadow-lg"
          style={{
            borderColor: "var(--colour-border)",
            backgroundColor: "var(--colour-surface)",
          }}
        >
          <div
            className="text-[10px] uppercase tracking-wider font-bold mb-1"
            style={{ color: chip.colour_hex }}
          >
            {chip.rarity}
          </div>
          <div className="text-[13px] font-semibold">{chip.name}</div>
          <div className="text-[12px] text-[var(--colour-text-secondary)] mt-0.5">
            {chip.description}
          </div>
        </div>
      )}
    </div>
  );
}
